/**
 * Enterprise Architecture Controller
 * Page controller for the Enterprise Architecture view with 10 sub-tabs
 *
 * Following OOA/OOD principles:
 * - Template Method Pattern: Extends BaseController
 * - Strategy Pattern: Each sub-tab has its own load/render strategy
 * - Lazy Loading: Tab data fetched only on first activation
 * - Single Responsibility: Orchestrates EA page interactions
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { EAExcelParser } from '../../ea/ea-excel-parser';
import type { ParseResult } from '../../ea/ea-excel-parser';
import { OverviewRenderer, type OverviewData, type EAStatistics } from '../../ea/renderers/overview-renderer';
import { OrgTopologyRenderer, type EAOrganizationEntity } from '../../ea/renderers/org-topology-renderer';
import { ConstellationVisualizer } from '../../ea/constellation-visualizer';
import { FullviewConstellationVisualizer } from '../../ea/fullview-constellation-visualizer';
import { FullviewConstellationDataBuilder } from '../../ea/fullview-constellation-data-builder';
import { DiagramGenerator } from '../../ea/diagram-generator';
import { DiagramStore } from '../../ea/diagram-store';

/**
 * Valid EA sub-tab names
 */
type EATabName =
    | 'overview'
    | 'organization'
    | 'capabilities'
    | 'services'
    | 'applications'
    | 'integrations'
    | 'processes'
    | 'infrastructure'
    | 'data'
    | 'import';

/** All panel IDs matching the HTML structure */
const EA_PANEL_IDS: EATabName[] = [
    'overview', 'organization', 'capabilities', 'services',
    'applications', 'integrations', 'processes', 'infrastructure',
    'data', 'import'
];

/** Tabs that display AI-generated diagrams */
const DIAGRAM_TABS: EATabName[] = [
    'capabilities', 'services', 'applications', 'integrations',
    'processes', 'infrastructure', 'data'
];

/** Map of tab names to their container element IDs */
const TAB_CONTAINER_IDS: Record<string, string> = {
    capabilities: 'ea-capabilities-container',
    services: 'ea-services-table-container',
    applications: 'ea-apps-table-container',
    integrations: 'ea-integrations-container',
    processes: 'ea-processes-container',
    infrastructure: 'ea-infra-container',
    data: 'ea-data-table-container',
};

/**
 * EAController manages the enterprise-architecture page
 * with 10 sub-tabs for viewing and importing EA data.
 */
export class EAController extends BaseController {
    /** Tracks which tabs have already been loaded (lazy loading) */
    private loadedTabs: Set<string> = new Set();

    /** Currently active tab name */
    private activeTab: EATabName = 'overview';

    /** Cached parsed data for import */
    private parsedImportData: ParseResult | null = null;

    /** Overview renderer instance */
    private overviewRenderer: OverviewRenderer | null = null;

    /** Organization topology renderer instance */
    private orgRenderer: OrgTopologyRenderer | null = null;

    /** Constellation visualizer instance (fullview or legacy) */
    private constellationVisualizer: FullviewConstellationVisualizer | ConstellationVisualizer | null = null;

    /** AI diagram generator */
    private diagramGenerator: DiagramGenerator;

    /** Diagram storage client */
    private diagramStore: DiagramStore;

    constructor(services: ControllerServices) {
        super(services);
        this.diagramGenerator = new DiagramGenerator(this.canisterClient);
        this.diagramStore = new DiagramStore(this.canisterClient);
    }

    getPageName(): PageName {
        return 'enterprise-architecture' as PageName;
    }

    protected async doInit(): Promise<void> {
        console.log('[EA] doInit starting, activeTab:', this.activeTab);
        this.setupSubTabs();
        console.log('[EA] setupSubTabs done, activeTab now:', this.activeTab);
        await this.loadTabData(this.activeTab);
        console.log('[EA] loadTabData done for:', this.activeTab);
    }

    protected setupEventListeners(): void {
        // Sub-tab buttons
        const tabButtons = document.querySelectorAll<HTMLElement>('.ea-tab');
        tabButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const tabName = button.getAttribute('data-ea-tab');
                if (tabName) {
                    this.switchTab(tabName as EATabName);
                }
            });
        });

        // Import: file upload zone click → trigger file input
        const uploadZone = document.getElementById('ea-upload-zone');
        const fileInput = document.getElementById('ea-file-input') as HTMLInputElement | null;
        if (uploadZone && fileInput) {
            uploadZone.addEventListener('click', () => fileInput.click());
            uploadZone.addEventListener('dragover', (e) => {
                e.preventDefault();
                uploadZone.classList.add('border-blue-400');
            });
            uploadZone.addEventListener('dragleave', () => {
                uploadZone.classList.remove('border-blue-400');
            });
            uploadZone.addEventListener('drop', (e) => {
                e.preventDefault();
                uploadZone.classList.remove('border-blue-400');
                const files = (e as DragEvent).dataTransfer?.files;
                if (files && files.length > 0) {
                    fileInput.files = files;
                    fileInput.dispatchEvent(new Event('change'));
                }
            });
            fileInput.addEventListener('change', () => this.handleFileSelected());
        }

        // Import button
        const importBtn = document.getElementById('ea-import-btn');
        if (importBtn) {
            importBtn.addEventListener('click', () => this.handleImport());
        }

        // Cancel button
        const cancelBtn = document.getElementById('ea-cancel-btn');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.cancelImport());
        }

        // Clear data button
        const clearBtn = document.getElementById('ea-clear-data-btn');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => this.handleClearData());
        }
    }

    protected doCleanup(): void {
        this.loadedTabs.clear();
        this.activeTab = 'overview';
        this.parsedImportData = null;
        this.overviewRenderer?.destroy();
        this.overviewRenderer = null;
        this.orgRenderer?.destroy();
        this.orgRenderer = null;
        this.constellationVisualizer?.destroy();
        this.constellationVisualizer = null;
    }

    // ----------------------------------------------------------------
    // Sub-tab switching
    // ----------------------------------------------------------------

    private setupSubTabs(): void {
        const activeButton = document.querySelector<HTMLElement>('.ea-tab.active');
        if (activeButton) {
            const tabName = activeButton.getAttribute('data-ea-tab');
            if (tabName) {
                this.activeTab = tabName as EATabName;
            }
        }
    }

    /**
     * Switch to a different sub-tab.
     * Shows the matching panel, hides others, and lazy-loads data.
     */
    private async switchTab(tabName: EATabName): Promise<void> {
        console.log('[EA] switchTab:', tabName);
        this.activeTab = tabName;

        // Update tab button active states + styling
        const tabButtons = document.querySelectorAll<HTMLElement>('.ea-tab');
        tabButtons.forEach((button) => {
            const btnTab = button.getAttribute('data-ea-tab');
            if (btnTab === tabName) {
                button.classList.add('active', 'border-blue-600', 'text-blue-600');
                button.classList.remove('border-transparent', 'text-slate-500');
            } else {
                button.classList.remove('active', 'border-blue-600', 'text-blue-600');
                button.classList.add('border-transparent', 'text-slate-500');
            }
        });

        // Show/hide panels using the known panel IDs
        for (const panelName of EA_PANEL_IDS) {
            const panel = document.getElementById(`ea-${panelName}`);
            if (!panel) {
                console.warn(`[EA] Panel not found: ea-${panelName}`);
                continue;
            }
            if (panelName === tabName) {
                panel.classList.remove('hidden');
                console.log(`[EA] Showing panel: ea-${panelName}, hidden=${panel.classList.contains('hidden')}, offsetHeight=${panel.offsetHeight}, innerHTML length=${panel.innerHTML.length}`);
            } else {
                panel.classList.add('hidden');
            }
        }

        // S43.6: Force reload organization tab if there's a pending session filter
        if (tabName === 'organization' && (window as any).pendingConstellationSessionId) {
            this.loadedTabs.delete('organization');
        }

        // Lazy load data if this tab has not been loaded before
        if (!this.loadedTabs.has(tabName)) {
            await this.loadTabData(tabName);
        }
    }

    /**
     * Load data for a specific tab (only called once per tab unless cleared)
     */
    private async loadTabData(tabName: EATabName): Promise<void> {
        try {
            switch (tabName) {
                case 'overview':
                    await this.loadOverviewTab();
                    break;
                case 'import':
                    // Import tab setup is handled by setupEventListeners
                    break;
                case 'organization':
                    await this.loadOrganizationTab();
                    break;
                case 'capabilities':
                case 'services':
                case 'applications':
                case 'integrations':
                case 'processes':
                case 'infrastructure':
                case 'data':
                    await this.loadDiagramTab(tabName);
                    break;
            }
            this.loadedTabs.add(tabName);
        } catch (error) {
            console.error(`Failed to load EA tab "${tabName}":`, error);
        }
    }

    // ----------------------------------------------------------------
    // Overview tab
    // ----------------------------------------------------------------

    private async loadOverviewTab(): Promise<void> {
        const container = document.getElementById('ea-overview-render-target');
        if (!container) {
            console.warn('[EA] loadOverviewTab: ea-overview-render-target not found');
            return;
        }

        try {
            console.log('[EA] loadOverviewTab: fetching statistics...');
            // Fetch statistics
            const stats = await this.canisterClient.callCanister<EAStatistics>(
                'vector_db', 'get_ea_statistics', []
            );
            console.log('[EA] loadOverviewTab: stats:', JSON.stringify(stats, (_k, v) => typeof v === 'bigint' ? Number(v) : v));

            // Fetch entity lists for Zachman matrix population
            console.log('[EA] loadOverviewTab: fetching entity lists...');
            const [orgs, apps, techSvcs, bizSvcs, dataObjs, hrRoles] = await Promise.all([
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_organizations', []).catch(() => []),
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_applications', []).catch(() => []),
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_technical_services', []).catch(() => []),
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_business_services', []).catch(() => []),
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_data_objects', []).catch(() => []),
                this.canisterClient.callCanister<Array<Record<string, unknown>>>('vector_db', 'list_ea_hr_roles', []).catch(() => []),
            ]);

            const data: OverviewData = {
                stats: stats as EAStatistics,
                organizations: orgs as Array<Record<string, unknown>>,
                applications: apps as Array<Record<string, unknown>>,
                technicalServices: techSvcs as Array<Record<string, unknown>>,
                businessServices: bizSvcs as Array<Record<string, unknown>>,
                dataObjects: dataObjs as Array<Record<string, unknown>>,
                hrRoles: hrRoles as Array<Record<string, unknown>>,
            };

            console.log('[EA] loadOverviewTab: entity counts — orgs:', (orgs as unknown[])?.length, 'apps:', (apps as unknown[])?.length, 'techSvcs:', (techSvcs as unknown[])?.length, 'bizSvcs:', (bizSvcs as unknown[])?.length, 'dataObjs:', (dataObjs as unknown[])?.length, 'hrRoles:', (hrRoles as unknown[])?.length);

            if (!this.overviewRenderer) {
                this.overviewRenderer = new OverviewRenderer(container);
            }
            this.overviewRenderer.render(data);
            console.log('[EA] loadOverviewTab: render complete, container innerHTML length:', container.innerHTML.length);
        } catch (error) {
            console.error('[EA] Failed to load EA overview:', error);
            container.innerHTML = '<div style="padding:24px;color:#94a3b8;text-align:center;">Failed to load overview data.</div>';
        }
    }

    // ----------------------------------------------------------------
    // Entity list tabs
    // ----------------------------------------------------------------

    private async loadOrganizationTab(): Promise<void> {
        const container = document.getElementById('ea-org-render-target');
        if (!container) return;
        container.innerHTML = '<div style="padding:24px;color:#94a3b8;text-align:center;">Loading EA constellation...</div>';

        try {
            // S43.6: Check for pending session filter from Ask Ada navigation
            const sessionId = this.consumePendingSessionId();

            // Sprint 49: Use FullviewConstellationDataBuilder with validation
            const fullviewBuilder = new FullviewConstellationDataBuilder(this.canisterClient);
            const { data, validation } = await fullviewBuilder.buildAndValidate(sessionId);

            if (data.nodes.length === 0) {
                const msg = sessionId
                    ? 'No entities found for this import session.'
                    : 'No EA data found. Import an EA spreadsheet first.';
                container.innerHTML = `<div style="padding:24px;color:#94a3b8;text-align:center;">${msg}</div>`;
                return;
            }

            // Ensure container has sufficient height for Cytoscape
            container.style.minHeight = '600px';

            // Destroy previous visualizer so it re-renders cleanly with new data
            this.constellationVisualizer?.destroy();

            // Sprint 49: Use new fullview visualizer with concentric layout + visual hierarchy
            const visualizer = new FullviewConstellationVisualizer('ea-org-render-target');
            this.constellationVisualizer = visualizer;

            if (!validation.valid) {
                visualizer.renderError(validation);
            } else {
                await visualizer.render(data);
            }
        } catch (error) {
            console.error('Failed to load EA constellation:', error);
            container.innerHTML = `<div style="padding:24px;color:#ef4444;text-align:center;">Failed to load: ${this.escapeHtml(String(error))}</div>`;
        }
    }

    /**
     * S43.6: Consume pending session ID set by Ask Ada's "View in Constellation" button.
     * Clears the value after reading so subsequent tab loads show all data.
     */
    private consumePendingSessionId(): string | undefined {
        const sessionId = (window as any).pendingConstellationSessionId as string | undefined;
        if (sessionId) {
            delete (window as any).pendingConstellationSessionId;
            // Force reload of organization tab even if previously loaded
            this.loadedTabs.delete('organization');
        }
        return sessionId;
    }

    // ----------------------------------------------------------------
    // AI-generated diagram tabs
    // ----------------------------------------------------------------

    /** Human-readable labels for each diagram tab */
    private static readonly TAB_LABELS: Record<string, string> = {
        capabilities: 'Business Capability Model',
        services: 'Service Architecture',
        applications: 'Application Integration Map',
        integrations: 'Integration Patterns & API Catalogue',
        processes: 'Business Process Flows',
        infrastructure: 'Infrastructure Deployment Topology',
        data: 'Data Architecture',
    };

    /**
     * Load a diagram tab: show the AI-generated SVG if one exists,
     * otherwise display a notice directing the user to Ask Ada.
     */
    private async loadDiagramTab(tabName: EATabName): Promise<void> {
        const containerId = TAB_CONTAINER_IDS[tabName];
        if (!containerId) return;

        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = '<p class="p-4 text-slate-500">Loading diagram...</p>';

        try {
            const svg = await this.diagramStore.get(tabName);
            if (svg) {
                container.innerHTML = svg;
            } else {
                const label = EAController.TAB_LABELS[tabName] ?? tabName;
                container.innerHTML = this.buildNoDiagramNotice(label);
            }
        } catch (error) {
            console.error(`[EA] Failed to load diagram for ${tabName}:`, error);
            const label = EAController.TAB_LABELS[tabName] ?? tabName;
            container.innerHTML = this.buildNoDiagramNotice(label);
        }
    }

    /**
     * Build the HTML notice shown when no AI diagram is available.
     * Directs the user to Ask Ada for interactive diagram generation.
     */
    private buildNoDiagramNotice(diagramLabel: string): string {
        return `
            <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 24px;text-align:center;">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:16px;">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
                <h3 style="color:#e2e8f0;font-size:18px;font-weight:600;margin:0 0 8px 0;">${diagramLabel}</h3>
                <p style="color:#94a3b8;font-size:14px;margin:0 0 16px 0;max-width:480px;">
                    No diagram has been generated yet. Import an EA spreadsheet to auto-generate diagrams,
                    or ask Ada to create one interactively.
                </p>
                <button
                    onclick="document.querySelector('[data-page=\\'ii-agent\\']')?.click();"
                    style="display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#3b82f6;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:500;cursor:pointer;"
                    onmouseover="this.style.background='#2563eb'" onmouseout="this.style.background='#3b82f6'"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    Ask Ada
                </button>
            </div>
        `;
    }

    // ----------------------------------------------------------------
    // Import tab handlers
    // ----------------------------------------------------------------

    private async handleFileSelected(): Promise<void> {
        const fileInput = document.getElementById('ea-file-input') as HTMLInputElement | null;
        const previewSection = document.getElementById('ea-parse-preview');
        const summaryContainer = document.getElementById('ea-parse-summary');
        const errorsContainer = document.getElementById('ea-parse-errors');

        if (!fileInput || !previewSection || !summaryContainer) return;

        const file = fileInput.files?.[0];
        if (!file) return;

        try {
            const buffer = await file.arrayBuffer();
            const parser = new EAExcelParser();
            const result = parser.parse(buffer);
            this.parsedImportData = result;

            // Show preview
            previewSection.classList.remove('hidden');

            const data = result.data;
            const counts = [
                { label: 'Organizations', count: data.organizations.length },
                { label: 'Applications', count: data.applications.length },
                { label: 'Technical Services', count: data.technical_services.length },
                { label: 'Business Services', count: data.business_services.length },
                { label: 'Data Objects', count: data.data_objects.length },
                { label: 'HR Roles', count: data.hr_roles.length },
                { label: 'Relationships', count: data.relationships.length },
            ];

            summaryContainer.innerHTML = counts
                .map(c => `<div class="bg-slate-50 rounded p-2">
                    <div class="text-lg font-bold text-slate-800">${c.count}</div>
                    <div class="text-xs text-slate-500">${c.label}</div>
                </div>`)
                .join('');

            // Show errors if any
            if (errorsContainer && result.summary.errors.length > 0) {
                errorsContainer.classList.remove('hidden');
                errorsContainer.textContent = `${result.summary.errors.length} warning(s): ${result.summary.errors.slice(0, 3).map(e => `${e.sheet} row ${e.row}: ${e.message}`).join('; ')}`;
            } else if (errorsContainer) {
                errorsContainer.classList.add('hidden');
            }
        } catch (error) {
            console.error('Failed to parse EA file:', error);
            if (summaryContainer) {
                summaryContainer.innerHTML = `<div class="col-span-4 text-red-500">Failed to parse file: ${this.escapeHtml(String(error))}</div>`;
            }
            previewSection?.classList.remove('hidden');
        }
    }

    private async handleImport(): Promise<void> {
        if (!this.parsedImportData) return;

        const importBtn = document.getElementById('ea-import-btn') as HTMLButtonElement | null;
        const resultSection = document.getElementById('ea-import-result');
        const resultContent = document.getElementById('ea-import-result-content');

        if (importBtn) {
            importBtn.disabled = true;
            importBtn.textContent = 'Importing...';
        }

        try {
            // Step 1: Bulk import EA data to canister
            const result = await this.canisterClient.callCanister<Record<string, unknown>>(
                'vector_db',
                'bulk_import_ea',
                [this.parsedImportData.data]
            );

            if (resultSection) resultSection.classList.remove('hidden');
            if (resultContent) {
                const r = result as Record<string, unknown>;
                resultContent.innerHTML = `
                    <div class="text-green-600 font-semibold mb-2">${r.success ? 'Import Successful' : 'Import Completed with Issues'}</div>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>Organizations: <strong>${r.organizations_created ?? 0}</strong></div>
                        <div>Applications: <strong>${r.applications_created ?? 0}</strong></div>
                        <div>Tech Services: <strong>${r.technical_services_created ?? 0}</strong></div>
                        <div>Biz Services: <strong>${r.business_services_created ?? 0}</strong></div>
                        <div>Data Objects: <strong>${r.data_objects_created ?? 0}</strong></div>
                        <div>HR Roles: <strong>${r.hr_roles_created ?? 0}</strong></div>
                        <div>Relationships: <strong>${r.relationships_created ?? 0}</strong></div>
                    </div>
                    ${(r.errors as string[] ?? []).length > 0 ? `<div class="mt-2 text-red-500 text-sm">${(r.errors as string[]).join('; ')}</div>` : ''}
                    <div class="mt-2 text-slate-500 text-sm">${r.message ?? ''}</div>
                `;
            }

            // Step 2: Generate AI diagrams
            if (importBtn) {
                importBtn.textContent = 'Generating diagrams...';
            }

            const diagramResult = await this.diagramGenerator.generateAll(
                this.parsedImportData.data,
                (tab, index, total) => {
                    if (importBtn) {
                        importBtn.textContent = `Generating diagram ${index}/${total}: ${tab}...`;
                    }
                }
            );

            // Clear loaded tabs so they reload with fresh diagrams
            this.loadedTabs.clear();
            this.loadedTabs.add('import');

            // Build summary toast
            const r2 = result as Record<string, unknown>;
            const totalEntities = (Number(r2.organizations_created ?? 0))
                + (Number(r2.applications_created ?? 0))
                + (Number(r2.technical_services_created ?? 0))
                + (Number(r2.business_services_created ?? 0))
                + (Number(r2.data_objects_created ?? 0))
                + (Number(r2.hr_roles_created ?? 0));
            const rels = Number(r2.relationships_created ?? 0);

            let diagramMsg = `${diagramResult.succeeded.length}/7 diagrams generated`;
            if (diagramResult.failed.length > 0) {
                diagramMsg += ` (${diagramResult.failed.length} failed)`;
            }

            // Append diagram results to the result content
            if (resultContent) {
                resultContent.innerHTML += `
                    <div class="mt-3 pt-3 border-t border-slate-200">
                        <div class="text-sm font-medium text-slate-700">AI Diagrams: ${diagramMsg}</div>
                        ${diagramResult.failed.length > 0
                            ? `<div class="text-xs text-amber-600 mt-1">${diagramResult.failed.map(f => `${f.tab}: ${f.error}`).join('; ')}</div>`
                            : '<div class="text-xs text-green-600 mt-1">All diagrams generated successfully</div>'
                        }
                    </div>
                `;
            }

            (window as any).toastNotifications?.success(
                `EA imported: ${totalEntities} entities, ${rels} relationships. ${diagramMsg}.`,
                { autoHideDelay: 6000 }
            );

        } catch (error) {
            console.error('EA import failed:', error);
            if (resultSection) resultSection.classList.remove('hidden');
            if (resultContent) {
                resultContent.innerHTML = `<div class="text-red-500">Import failed: ${this.escapeHtml(String(error))}</div>`;
            }
            (window as any).toastNotifications?.error(
                `EA import failed: ${String(error)}`,
            );
        } finally {
            if (importBtn) {
                importBtn.disabled = false;
                importBtn.textContent = 'Import to Canister';
            }
        }
    }

    private cancelImport(): void {
        this.parsedImportData = null;
        const previewSection = document.getElementById('ea-parse-preview');
        if (previewSection) previewSection.classList.add('hidden');
        const fileInput = document.getElementById('ea-file-input') as HTMLInputElement | null;
        if (fileInput) fileInput.value = '';
    }

    private async handleClearData(): Promise<void> {
        if (!confirm('Are you sure you want to clear all EA data? This cannot be undone.')) return;

        try {
            const result = await this.canisterClient.callCanister<string>(
                'vector_db', 'clear_ea_data', []
            );
            console.log('EA data cleared:', result);

            // Destroy dynamic renderers
            this.overviewRenderer?.destroy();
            this.overviewRenderer = null;
            this.orgRenderer?.destroy();
            this.orgRenderer = null;
            this.constellationVisualizer?.destroy();
            this.constellationVisualizer = null;

            // Clear dynamic data containers within each panel
            for (const containerId of Object.values(TAB_CONTAINER_IDS)) {
                const container = document.getElementById(containerId);
                if (container) {
                    container.innerHTML = '<p class="p-6 text-slate-400 text-center">Import EA data to generate this diagram.</p>';
                }
            }

            // Reset import UI state
            this.parsedImportData = null;
            const previewSection = document.getElementById('ea-parse-preview');
            if (previewSection) previewSection.classList.add('hidden');
            const resultSection = document.getElementById('ea-import-result');
            if (resultSection) resultSection.classList.add('hidden');
            const fileInput = document.getElementById('ea-file-input') as HTMLInputElement | null;
            if (fileInput) fileInput.value = '';

            this.loadedTabs.clear();
            // Reload overview to show empty state
            await this.loadOverviewTab();

            (window as any).toastNotifications?.success(
                'All EA data and diagrams have been cleared.',
                { autoHideDelay: 4000 }
            );
        } catch (error) {
            console.error('Failed to clear EA data:', error);
            (window as any).toastNotifications?.error(
                `Failed to clear EA data: ${String(error)}`,
            );
        }
    }

    // ----------------------------------------------------------------
    // Table rendering utility
    // ----------------------------------------------------------------

    private renderTable(
        container: HTMLElement,
        entities: Array<Record<string, unknown>>,
        columns: string[]
    ): void {
        const headerCells = columns
            .map(col => `<th class="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">${this.escapeHtml(this.formatColumnName(col))}</th>`)
            .join('');

        const rows = entities
            .map(entity => {
                const cells = columns
                    .map(col => {
                        const val = entity[col];
                        const display = val === null || val === undefined ? '' : String(val);
                        return `<td class="px-3 py-2 text-sm text-slate-700 whitespace-nowrap">${this.escapeHtml(display)}</td>`;
                    })
                    .join('');
                return `<tr class="border-b border-slate-100 hover:bg-slate-50">${cells}</tr>`;
            })
            .join('');

        container.innerHTML = `
            <table class="min-w-full divide-y divide-slate-200">
                <thead class="bg-slate-50">
                    <tr>${headerCells}</tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-100">
                    ${rows}
                </tbody>
            </table>
            <div class="p-2 text-xs text-slate-400">${entities.length} record${entities.length !== 1 ? 's' : ''}</div>
        `;
    }

    /**
     * Convert snake_case column names to human-readable labels
     */
    private formatColumnName(name: string): string {
        return name
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, c => c.toUpperCase());
    }
}
